import { forwardRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import clsx from 'clsx';

const PasswordInput = forwardRef(
  (
    {
      label,
      error,
      helperText,
      showStrength = false,
      fullWidth = false,
      className = '',
      containerClassName = '',
      strengthValue = '',
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);

    const inputClasses = clsx(
      'px-4 py-2 pr-12 border rounded-lg transition-colors duration-200',
      'focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent',
      'disabled:bg-gray-100 disabled:cursor-not-allowed',
      error
        ? 'border-secondary-500 focus:ring-secondary-400'
        : 'border-gray-300',
      'bg-white text-primary-900',
      'placeholder:text-gray-400',
      '[&::-ms-reveal]:hidden [&::-ms-clear]:hidden [&::-webkit-credentials-auto-fill-button]:hidden [&::-webkit-contacts-auto-fill-button]:hidden',
      fullWidth && 'w-full',
      className
    );

    // Password strength calculation
    const getPasswordStrength = (password) => {
      if (!password) return { score: 0, label: '', color: '' };
      
      let score = 0;
      if (password.length >= 8) score++;
      if (/[a-z]/.test(password)) score++;
      if (/[A-Z]/.test(password)) score++;
      if (/[0-9]/.test(password)) score++;
      if (/[^a-zA-Z0-9]/.test(password)) score++;

      if (score <= 2) return { score, label: 'Weak', color: 'bg-red-500' };
      if (score === 3) return { score, label: 'Fair', color: 'bg-yellow-500' };
      if (score === 4) return { score, label: 'Good', color: 'bg-blue-500' };
      return { score, label: 'Strong', color: 'bg-green-500' };
    };

    const strength = showStrength ? getPasswordStrength(strengthValue) : null;

    return (
      <div className={clsx('flex flex-col', fullWidth && 'w-full', containerClassName)}>
        {label && (
          <label className="mb-1.5 text-sm font-medium text-primary-700">
            {label}
          </label>
        )}
        
        <div className="relative">
          <input
            ref={ref}
            type={showPassword ? 'text' : 'password'}
            className={inputClasses}
            data-lpignore="true"
            data-form-type="other"
            autoComplete="off"
            {...props}
          />
          
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>

        {showStrength && strengthValue && (
          <div className="mt-2">
            <div className="flex gap-1 mb-1">
              {[1, 2, 3, 4, 5].map((level) => (
                <div
                  key={level}
                  className={clsx(
                    'h-1 flex-1 rounded-full transition-colors',
                    level <= strength.score ? strength.color : 'bg-gray-200'
                  )}
                />
              ))}
            </div>
            <p className="text-xs text-gray-600">
              Password strength: <span className="font-medium">{strength.label}</span>
            </p>
          </div>
        )}
        
        {(error || helperText) && (
          <p
            className={clsx(
              'mt-1.5 text-sm',
              error ? 'text-secondary-600' : 'text-gray-500'
            )}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
